/**
 * @format
 */
import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, HostListener, Inject, Input, OnInit, Signal, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '@uirouter/angular';
import {
    CreatePaymentRequest,
    Education,
    FeatureToggles,
    LocalizedString,
    LocalizedUrl,
    PayerDetails,
    Plan,
    SearchResult,
    StudyRight,
    StudyRightExtensionPaymentConfiguration,
    StudyRightExtensionWorkflowApplication,
    UniversitySettings,
    Workflow,
} from 'common-typescript/types';
import { catchError, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { take } from 'rxjs/operators';
import { AlertsService } from 'sis-components/alerts/alerts-ng.service';
import { AppErrorHandler } from 'sis-components/error-handler/app-error-handler';
import { UiRouterService } from 'sis-components/router/ui-router.service';
import { StudyRightExtensionPaymentService } from 'sis-components/service/study-right-extension-payment-service';
import { UniversityService } from 'sis-components/service/university.service';
import { WorkflowEntityService } from 'sis-components/service/workflow-entity.service';
import { paymentStartedWarningDialogOpener } from '../payment-started-warning-dialog/payment-started-warning-dialog.component';
import { StudyRightExtensionApplicationService } from './study-right-extension-application.service';
import { AutomaticallyFilledExtensionApplicationFields, ExtensionApplicationFormData, FilledExtensionApplication, PlanData } from './types';

type Step = 'application' | 'preview' | 'payerDetails';

@Component({
    selector: 'app-create-extension-application',
    templateUrl: './create-extension-application.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [StudyRightExtensionApplicationService],
})
export class CreateExtensionApplicationComponent implements OnInit {
    @Input() plan: Plan;
    @Input() studyRight: StudyRight;
    @Input() education: Education;

    readonly planData = signal<PlanData>(null);
    readonly automaticallyFilledApplicationFields = signal<AutomaticallyFilledExtensionApplicationFields>(null);
    readonly filledExtensionApplication = signal<FilledExtensionApplication>(null);

    private readonly _currentStep = signal<Step>('application');
    get currentStep(): Signal<Step> {
        return this._currentStep.asReadonly();
    }

    /**
     * If true, a native browser confirmation dialog will be shown when attempting to leave the page via browser/DOM interactions
     * (e.g. refreshing/closing the tab, or setting `document.location.href`). Does not affect UI-Router navigation.
     */
    hasUnsavedChanges = false;
    isFormSubmitted = false;
    private readonly openPaymentStartedWarningDialog = paymentStartedWarningDialogOpener();
    universitySettings$: Observable<UniversitySettings>;

    constructor(
        private uiRouterService: UiRouterService,
        private appErrorHandler: AppErrorHandler,
        private workflowEntityService: WorkflowEntityService,
        private state: StateService,
        private alertsService: AlertsService,
        private universityService: UniversityService,
        private studyRightExtensionPaymentService: StudyRightExtensionPaymentService,
        private studyRightExtensionApplicationService: StudyRightExtensionApplicationService,
        private destroyRef: DestroyRef,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    ngOnInit(): void {
        this.studyRightExtensionApplicationService
            .getApplicationAndPlanData(this.plan, this.studyRight)
            .pipe(take(1), takeUntilDestroyed(this.destroyRef), this.appErrorHandler.defaultErrorHandler())
            .subscribe(data => {
                this.planData.set({
                    attainedCreditsByStudyTerm: data.attainedCreditsByStudyTerm,
                    creditsPerTerm: data.creditsPerTerm,
                    plannedStudies: data.plannedStudies,
                });
                this.automaticallyFilledApplicationFields.set(data.automaticallyFilledApplicationFields);
            });
        this.universitySettings$ = this.universityService.getCurrentUniversitySettings().pipe(
            this.appErrorHandler.defaultErrorHandler(), //
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.universitySettings$
            .pipe(
                take(1), // this is not useless, although we also use takeUntilDestroyed
                switchMap(settings => {
                    if ((<FeatureToggles | null>settings.featureToggles)?.studyRightExtensionFeeEnabled) {
                        const instructionsUrl: LocalizedUrl | null | undefined = (<StudyRightExtensionPaymentConfiguration | null>(
                            settings.studyRightExtensionPaymentConfiguration
                        ))?.instructionsUrl;
                        return this.checkForExistingDraftWorkflows(instructionsUrl);
                    }
                    return [];
                }),
                takeUntilDestroyed(this.destroyRef), // prevent the modal from opening if the request takes a while
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe();
    }

    @HostListener('window:beforeunload', ['$event'])
    confirmDiscardChanges(event: BeforeUnloadEvent) {
        if (this.hasUnsavedChanges) {
            event.preventDefault();
            event.returnValue = true;
        }
    }

    private checkForExistingDraftWorkflows(instructionsUrl: LocalizedString): Observable<SearchResult<Workflow>> {
        return this.workflowEntityService
            .search({
                limit: 1,
                documentState: 'DRAFT',
                workflowState: 'WAITING_FOR_PAYMENT',
                workflowType: 'STUDY_RIGHT_EXTENSION_WORKFLOW',
                studyRightId: this.studyRight.id,
            })
            .pipe(
                take(1),
                tap(result => {
                    if (result.searchResults.length > 0) {
                        this.showPaymentWarningDialog(instructionsUrl);
                    }
                }),
            );
    }

    private showPaymentWarningDialog(instructionsUrl: LocalizedString): void {
        this.openPaymentStartedWarningDialog({ instructionsUrl });
    }

    protected onStepChange(step: Step): void {
        this._currentStep.set(step);
        // Focus the page title when navigating between steps
        this.document.querySelector('h1')?.focus();
    }

    onApplicationFormContinue(formData: ExtensionApplicationFormData, studyRightExtensionFeeEnabled: boolean) {
        this.filledExtensionApplication.set({ ...this.automaticallyFilledApplicationFields(), ...formData });
        if (studyRightExtensionFeeEnabled) {
            this.onStepChange('preview');
        } else {
            this.submitFreeApplication();
        }
    }

    protected submitPaidApplication(payerDetails: Readonly<PayerDetails>) {
        if (this.isFormSubmitted || !this.filledExtensionApplication() || !payerDetails) {
            return;
        }
        this.isFormSubmitted = true;

        const createPaymentRequest: CreatePaymentRequest = {
            studyRightExtensionWorkflowApplication: this.filledExtensionApplication() as StudyRightExtensionWorkflowApplication,
            payerDetails,
        };
        this.studyRightExtensionPaymentService
            .createPayment(createPaymentRequest)
            .pipe(take(1), this.reEnableApplicationSubmitAfterError(), this.appErrorHandler.defaultErrorHandler())
            .subscribe(createPaymentResponse => {
                // All changes have been saved, disable the native confirmation dialog before navigating to the payment system
                this.hasUnsavedChanges = false;
                this.redirectTo(createPaymentResponse.paymentAddress);
            });
    }

    private submitFreeApplication() {
        if (this.isFormSubmitted || !this.filledExtensionApplication()) {
            return;
        }
        this.isFormSubmitted = true;

        this.workflowEntityService
            .createAsStudent(this.filledExtensionApplication())
            .pipe(take(1), this.reEnableApplicationSubmitAfterError(), this.appErrorHandler.defaultErrorHandler())
            .subscribe(savedStudentWorkflow => {
                // No need to unset `hasUnsavedChanges`, as it doesn't affect UI-Router navigation
                this.alertsService.success('PROFILE.APPLICATIONS.APPLICATION_SENT');
                this.state.go('student.logged-in.profile.applications.study-right-extension-workflow', {
                    applicationId: savedStudentWorkflow.id,
                });
            });
    }

    private reEnableApplicationSubmitAfterError<T>() {
        return catchError<T, never>(e => {
            this.isFormSubmitted = false;
            throw e;
        });
    }

    redirectTo(url: string): void {
        this.document.location.href = url;
    }

    exitApplication(): void {
        this.uiRouterService.goToPreviousState();
    }
}
